import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FiActivity } from 'react-icons/fi';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaSyncAlt,
  FaClipboardList,
  FaRedo,
  FaUndoAlt,
  FaTrophy,
  FaClock,
  FaPlay,
  FaCogs,
  FaExternalLinkAlt,
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaLockOpen,
  FaUsers,
  FaUserEdit,
  FaSearch,
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';

interface MatchProgress {
  matchId: string;
  localTeam?: string;
  visitorTeam?: string;
  scoreUpdated: boolean;
  fantasyPoints: boolean;
  state?: string;
  // null = no MoM expected (match doesn't have MoM published yet, or it's
  // a no-result / abandoned match). true = man_of_match bonus is present
  // in this contest's fantasy-points row. false = MoM is published in
  // Cricbuzz but the bonus is NOT applied to this contest yet.
  momExpected?: boolean;
  momApplied?: boolean | null;
}

interface ContestProgress {
  contestName: string;
  contestId: string;
  matches: MatchProgress[];
}

const LIVE_STATES = ['Live', 'In Progress'];

const Tracker: React.FC = () => {
  const [series, setSeries] = useState<any[]>([]);
  const [selectedSeries, setSelectedSeries] = useState<string>('');
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [contestsProgress, setContestsProgress] = useState<ContestProgress[]>([]);

  // Series-level manual actions
  const [processingComplete, setProcessingComplete] = useState(false);
  const [syncingScorecard, setSyncingScorecard] = useState(false);
  const [updatingFantasyPoints, setUpdatingFantasyPoints] = useState(false);
  const [revertingSeriesPoints, setRevertingSeriesPoints] = useState(false);
  const [manualActionsOpen, setManualActionsOpen] = useState(false);

  // Contest-level manual actions
  const [rerunContestId, setRerunContestId] = useState<string | null>(null);
  const [revertingContestId, setRevertingContestId] = useState<string | null>(null);
  const [rebuildingContestId, setRebuildingContestId] = useState<string | null>(null);

  // Per-match manual actions (one in flight at a time is fine here)
  const [processingMatchId, setProcessingMatchId] = useState<string | null>(null);

  // Lock/unlock per contest
  const [togglingLockContestId, setTogglingLockContestId] = useState<string | null>(null);
  const [lockStatuses, setLockStatuses] = useState<Record<string, boolean>>({});

  // Manual MoM recovery sweep
  const [recoveringMom, setRecoveringMom] = useState(false);

  // Admin team-edit
  const [teamsListFor, setTeamsListFor] = useState<{ contestId: string; contestName: string } | null>(null);
  const [contestTeams, setContestTeams] = useState<any[]>([]);
  const [contestTeamsLoading, setContestTeamsLoading] = useState(false);
  const [editingTeam, setEditingTeam] = useState<any | null>(null);
  const [seriesSquad, setSeriesSquad] = useState<any[]>([]);
  const [seriesSquadLoading, setSeriesSquadLoading] = useState(false);
  // Match list for the same series — used to label the retroactive-apply
  // fromMatch dropdown ("M36 · MI vs GT") instead of raw matchIds.
  const [seriesMatches, setSeriesMatches] = useState<any[]>([]);

  // Audit log
  const [auditLogFor, setAuditLogFor] = useState<{ contestId: string; contestName: string } | null>(null);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const getMatchStateColors = (state?: string) => {
    switch (state) {
      case 'Complete':
        return {
          bg: 'bg-emerald-50 dark:bg-emerald-950/40',
          border: 'border-emerald-400 dark:border-emerald-600',
          badge: 'bg-emerald-500 text-white',
        };
      case 'Upcoming':
        return {
          bg: 'bg-sky-50 dark:bg-sky-950/40',
          border: 'border-sky-400 dark:border-sky-600',
          badge: 'bg-sky-500 text-white',
        };
      case 'Preview':
      case 'Toss':
        return {
          bg: 'bg-violet-50 dark:bg-violet-950/40',
          border: 'border-violet-400 dark:border-violet-600',
          badge: 'bg-violet-500 text-white',
        };
      case 'Live':
      case 'In Progress':
        return {
          bg: 'bg-rose-50 dark:bg-rose-950/40',
          border: 'border-rose-500 dark:border-rose-600',
          badge: 'bg-rose-500 text-white animate-pulse',
        };
      default:
        return {
          bg: 'bg-slate-50 dark:bg-slate-900/40',
          border: 'border-slate-300 dark:border-slate-600',
          badge: 'bg-slate-500 text-white',
        };
    }
  };

  const loadSeries = useCallback(async () => {
    try {
      setLoadingSeries(true);
      const response = await apiService.getSeries();
      const apiSeries = response?.data || response || [];
      const published = apiSeries
        .filter((s: any) => s.publish === true)
        .map((s: any) => ({ seriesId: s.seriesId || s.id, name: s.name || s.title }));
      setSeries(published);
    } catch (err) {
      toast.error('Failed to load series');
    } finally {
      setLoadingSeries(false);
    }
  }, []);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const loadProgress = useCallback(async () => {
    if (!selectedSeries) return;
    try {
      setLoadingProgress(true);
      const data = await apiService.getSeriesProgress(selectedSeries);
      const contestsArray = data?.contests || data || [];
      if (!Array.isArray(contestsArray)) {
        setContestsProgress([]);
        return;
      }
      const transformed: ContestProgress[] = contestsArray.map((contest: any) => {
        const matchProgressArray = contest.matchProgress || [];
        const matches: MatchProgress[] = matchProgressArray.map((match: any) => ({
          matchId: String(match.matchId),
          localTeam:
            match?.localTeam?.teamSName ||
            match?.localTeam?.teamName ||
            match?.localTeamName,
          visitorTeam:
            match?.visitorTeam?.teamSName ||
            match?.visitorTeam?.teamName ||
            match?.visitorTeamName,
          scoreUpdated: match.scoreUpdated || match.scoresUpdated || false,
          fantasyPoints: match.fantasyPointsUpdated || match.fantasyPoints || false,
          state: match.state || 'Unknown',
          momExpected: !!match.momExpected,
          momApplied:
            typeof match.momApplied === 'boolean' ? match.momApplied : null,
        }));
        return {
          contestName: contest.contestName,
          contestId: contest.contestId,
          matches,
        };
      });
      setContestsProgress(transformed);
    } catch (err) {
      toast.error('Failed to load progress');
      setContestsProgress([]);
    } finally {
      setLoadingProgress(false);
    }
  }, [selectedSeries]);

  useEffect(() => {
    if (selectedSeries) loadProgress();
  }, [selectedSeries, loadProgress]);

  // Fetch lock status for each contest so Lock/Unlock button shows the right state.
  useEffect(() => {
    if (contestsProgress.length === 0) return;
    let cancelled = false;
    Promise.all(
      contestsProgress.map((c) =>
        apiService
          .getContestLockStatus(c.contestId)
          .then((s: any) => [c.contestId, !!(s?.teamsLocked ?? s?.locked ?? s?.isLocked)] as const)
          .catch(() => [c.contestId, false] as const),
      ),
    ).then((entries) => {
      if (cancelled) return;
      setLockStatuses(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [contestsProgress]);

  const handleToggleLock = async (contestId: string, contestName: string, currentlyLocked: boolean) => {
    const action = currentlyLocked ? 'unlock' : 'lock';
    if (!window.confirm(`${currentlyLocked ? 'Unlock' : 'Lock'} teams for "${contestName}"?`)) return;
    setTogglingLockContestId(contestId);
    try {
      if (currentlyLocked) {
        await apiService.unlockContestTeams(contestId, `Admin ${action} via Tracker`);
      } else {
        try {
          await apiService.lockContestTeams(contestId, `Admin ${action} via Tracker`);
        } catch (err: any) {
          // Backend refuses to seal a transfer-enabled contest while
          // matches are still upcoming. If that's the rejection, ask
          // for a strong confirmation and retry with force=true. Any
          // other error bubbles up to the outer catch.
          const msg = String(err?.message || '');
          if (msg.includes('match(es) are still upcoming')) {
            const ok = window.confirm(
              `${msg}\n\nAre you SURE you want to seal "${contestName}" mid-series? ` +
              `This blocks all transfers and team edits for the rest of the season. ` +
              `Click OK to force-lock; Cancel to keep the contest active.`,
            );
            if (!ok) {
              toast.info('Lock cancelled');
              return;
            }
            await apiService.lockContestTeams(contestId, `Admin ${action} via Tracker (forced)`, true);
          } else {
            throw err;
          }
        }
      }
      setLockStatuses((prev) => ({ ...prev, [contestId]: !currentlyLocked }));
      toast.success(`Contest ${action}ed`);
    } catch (err: any) {
      toast.error(err?.message || `${action} failed`);
    } finally {
      setTogglingLockContestId(null);
    }
  };

  /**
   * Force-unlock bypasses the "cannot unlock series_clash after series
   * starts" guard but NOT the "match_clash is permanently locked"
   * invariant (the server will reject that with a 400). Prompts for a
   * reason so the action is auditable.
   */
  const handleForceUnlock = async (contestId: string, contestName: string) => {
    const confirmed = window.confirm(
      `Force unlock "${contestName}"?\n\nThis bypasses the normal "cannot unlock after series starts" safety check. ` +
        `Only use for fixing stuck contests or running tests.\n\n` +
        `Note: match_clash contests cannot be force-unlocked — per-match deadlines are permanent by design.`,
    );
    if (!confirmed) return;
    const reason = window.prompt('Reason (will be logged):', 'Admin force-unlock via Tracker');
    if (reason === null) return; // user cancelled the prompt
    setTogglingLockContestId(contestId);
    try {
      await apiService.forceUnlockContestTeams(contestId, reason || 'Admin force-unlock via Tracker');
      setLockStatuses((prev) => ({ ...prev, [contestId]: false }));
      toast.success('Contest force-unlocked');
    } catch (err: any) {
      toast.error(err?.message || 'Force unlock failed');
    } finally {
      setTogglingLockContestId(null);
    }
  };

  // ---- Global recovery sweeps ----

  const handleRecoverMom = async () => {
    if (!window.confirm(
      'Scan completed matches in the last 72h, re-fetch Cricbuzz for any with missing MoM, ' +
      'and re-apply the MoM bonus where it\'s still absent? ' +
      'Idempotent — does nothing for matches whose MoM bonus is already applied.',
    )) return;
    setRecoveringMom(true);
    try {
      const result = await apiService.recoverMissingMom(72);
      const fixed = result?.fixedCount ?? result?.fixed?.length ?? 0;
      const skipped = result?.skippedCount ?? result?.skipped?.length ?? 0;
      const failed = result?.failedCount ?? result?.failed?.length ?? 0;
      const scanned = result?.scanned ?? 0;
      const refetched = result?.refetchedCount ?? 0;
      if (fixed === 0 && failed === 0) {
        toast.info(`Scanned ${scanned} match(es), refetched ${refetched} — nothing to fix (${skipped} already correct/skipped).`);
      } else {
        toast.success(`Repaired ${fixed} contest(s), refetched ${refetched} match(es) (${skipped} already correct, ${failed} failed).`);
      }
      if (selectedSeries) loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'MoM recovery failed');
    } finally {
      setRecoveringMom(false);
    }
  };

  // ---- Series-level manual actions (escape hatches for automation failures) ----

  const handleProcessComplete = async () => {
    if (!selectedSeries) return;
    if (!window.confirm('Run full process-complete for this series? (sync scorecards + update fantasy points)')) return;
    setProcessingComplete(true);
    try {
      await apiService.processComplete(selectedSeries);
      toast.success('Process-complete finished');
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Process-complete failed');
    } finally {
      setProcessingComplete(false);
    }
  };

  const handleSyncScorecards = async () => {
    if (!selectedSeries) return;
    setSyncingScorecard(true);
    try {
      await apiService.syncScorecards(selectedSeries);
      toast.success('Scorecards synced');
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Scorecard sync failed');
    } finally {
      setSyncingScorecard(false);
    }
  };

  const handleUpdateFantasyPoints = async () => {
    if (!selectedSeries) return;
    setUpdatingFantasyPoints(true);
    try {
      await apiService.updateFantasyPoints(selectedSeries);
      toast.success('Fantasy points updated');
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Fantasy points update failed');
    } finally {
      setUpdatingFantasyPoints(false);
    }
  };

  const handleRevertSeriesPoints = async () => {
    if (!selectedSeries) return;
    if (!window.confirm('Revert ALL fantasy points for this entire series? This affects every contest in the series.')) return;
    setRevertingSeriesPoints(true);
    try {
      await apiService.revertFantasyPointsBySeries(selectedSeries);
      toast.success('Series points reverted');
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Revert failed');
    } finally {
      setRevertingSeriesPoints(false);
    }
  };

  // ---- Contest-level manual actions ----

  const handleForceRerun = async (contestId: string, contestName: string) => {
    if (!window.confirm(`Force rescore every match in "${contestName}"?`)) return;
    setRerunContestId(contestId);
    try {
      const result = await apiService.updateFantasyPointsByContest(contestId, { force: true });
      const processed = result?.processed ?? result?.data?.processed;
      toast.success(`Rerun queued for "${contestName}"${processed != null ? ` — ${processed} matches` : ''}`);
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Rerun failed');
    } finally {
      setRerunContestId(null);
    }
  };

  const handleRevertContest = async (contestId: string, contestName: string) => {
    if (!window.confirm(`Revert ALL fantasy points for "${contestName}"?`)) return;
    setRevertingContestId(contestId);
    try {
      await apiService.revertFantasyPointsByContest(contestId);
      toast.success(`Reverted "${contestName}"`);
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Revert failed');
    } finally {
      setRevertingContestId(null);
    }
  };

  const handleRebuildContest = async (contestId: string, contestName: string) => {
    if (!window.confirm(`Rebuild ALL fantasy points for "${contestName}" from scratch? This reverts every match and re-processes them in order.`)) return;
    setRebuildingContestId(contestId);
    try {
      const result = await apiService.rebuildContest(contestId, 'Admin rebuild via Tracker');
      toast.success(`Rebuild complete: ${result.processed}/${result.totalMatches} matches processed${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Rebuild failed');
    } finally {
      setRebuildingContestId(null);
    }
  };

  // ---- Per-match manual actions ----

  const matchKey = (contestId: string, matchId: string) => `${contestId}:${matchId}`;

  const handleProcessMatch = async (contestId: string, matchId: string) => {
    setProcessingMatchId(matchKey(contestId, matchId));
    try {
      await apiService.updateFantasyPointsByContest(contestId, { matchIds: [Number(matchId)], force: true });
      toast.success(`Match ${matchId} processed`);
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Match processing failed');
    } finally {
      setProcessingMatchId(null);
    }
  };

  const handleRevertMatch = async (contestId: string, matchId: string) => {
    if (!window.confirm(`Revert fantasy points for match ${matchId}?`)) return;
    setProcessingMatchId(matchKey(contestId, matchId));
    try {
      await apiService.revertFantasyPointsByMatch(selectedSeries, Number(matchId));
      toast.success(`Match ${matchId} reverted`);
      await loadProgress();
    } catch (err: any) {
      toast.error(err?.message || 'Match revert failed');
    } finally {
      setProcessingMatchId(null);
    }
  };

  // ---- Audit log ----

  // ---- Admin team edit ----

  const handleOpenTeamsList = async (contestId: string, contestName: string) => {
    setTeamsListFor({ contestId, contestName });
    setContestTeamsLoading(true);
    setContestTeams([]);
    try {
      const teams = await apiService.getContestFantasyTeams(contestId);
      setContestTeams(Array.isArray(teams) ? teams : []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load teams');
    } finally {
      setContestTeamsLoading(false);
    }
  };

  const handleOpenEditTeam = async (team: any) => {
    setEditingTeam(team);
    if (!selectedSeries) return;
    // Squad — needed for the player picker.
    if (seriesSquad.length === 0) {
      setSeriesSquadLoading(true);
      try {
        const squad = await apiService.getSquadsBySeries(selectedSeries);
        setSeriesSquad(Array.isArray(squad) ? squad : []);
      } catch (err: any) {
        toast.error(err?.message || 'Failed to load squad');
      } finally {
        setSeriesSquadLoading(false);
      }
    }
    // Matches — needed so the retroactive-apply fromMatch dropdown can
    // render "M36 · MI vs GT" instead of a raw matchId.
    if (seriesMatches.length === 0) {
      try {
        const matches = await apiService.getMatches(selectedSeries);
        setSeriesMatches(Array.isArray(matches) ? matches : []);
      } catch (err: any) {
        // Non-fatal — dialog falls back to raw matchId labels.
        console.warn('Failed to load matches for retro dropdown:', err?.message);
      }
    }
  };

  const handleSubmitTeamEdit = async (
    body: { players?: number[]; captainPlayerId?: number; viceCaptainPlayerId?: number; reason?: string; force?: boolean },
  ) => {
    if (!editingTeam || !teamsListFor) return;
    try {
      await apiService.editFantasyTeam(teamsListFor.contestId, editingTeam._id || editingTeam.id, body);
      toast.success(`Updated ${editingTeam.fantasyName}`);
      // Refresh teams list so the modal shows updated rosters
      const teams = await apiService.getContestFantasyTeams(teamsListFor.contestId);
      setContestTeams(Array.isArray(teams) ? teams : []);
      setEditingTeam(null);
    } catch (err: any) {
      toast.error(err?.message || 'Edit failed');
    }
  };

  /**
   * Retroactive-apply submit path. Used when the dialog has the
   * "Apply from match X" toggle on — the buffered-edit flow doesn't
   * help here because the relevant snapshot already exists.
   */
  const handleSubmitApplyFromMatch = async (body: {
    fromMatchId: number;
    players: number[];
    captainPlayerId?: number | null;
    viceCaptainPlayerId?: number | null;
    reason?: string;
  }) => {
    if (!editingTeam || !teamsListFor) return;
    try {
      const result = await apiService.applyTeamFromMatch(
        teamsListFor.contestId,
        editingTeam._id || editingTeam.id,
        body,
      );
      const moved = (result?.movedToNewLineup || []).length;
      const rescored = (result?.rescored || []).length;
      toast.success(
        `${editingTeam.fantasyName}: applied to ${moved} match${moved === 1 ? '' : 'es'}, re-scored ${rescored}.`,
      );
      const teams = await apiService.getContestFantasyTeams(teamsListFor.contestId);
      setContestTeams(Array.isArray(teams) ? teams : []);
      setEditingTeam(null);
    } catch (err: any) {
      toast.error(err?.message || 'Retroactive apply failed');
    }
  };

  const handleViewAuditLog = async (contestId: string, contestName: string) => {
    setAuditLogFor({ contestId, contestName });
    setAuditLoading(true);
    setAuditEntries([]);
    try {
      const data = await apiService.getContestAuditLog(contestId, 100);
      setAuditEntries(data?.entries || []);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  // ---- Summary aggregation ----

  const summary = useMemo(() => {
    let contests = contestsProgress.length;
    let liveContests = 0;
    let totalMatches = 0;
    let completeMatches = 0;
    let liveMatches = 0;
    let pointsReady = 0;

    for (const c of contestsProgress) {
      const hasLive = c.matches.some((m) => LIVE_STATES.includes(m.state || ''));
      if (hasLive) liveContests++;
      for (const m of c.matches) {
        totalMatches++;
        if (m.state === 'Complete') completeMatches++;
        if (LIVE_STATES.includes(m.state || '')) liveMatches++;
        if (m.fantasyPoints) pointsReady++;
      }
    }
    return { contests, liveContests, totalMatches, completeMatches, liveMatches, pointsReady };
  }, [contestsProgress]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ToastContainer position="top-right" autoClose={4000} theme="colored" />

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <FiActivity className="text-primary-500" />
            Live Scoring Tracker
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Per-contest scoring status. Scoring runs every 5 minutes automatically — manual controls below are for recovery when automation misbehaves.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRecoverMom}
            disabled={recoveringMom}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 hover:bg-amber-200 dark:hover:bg-amber-500/30 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Scan recently completed matches and re-apply the man-of-the-match bonus where missing"
          >
            {recoveringMom ? (
              <FaSyncAlt className="animate-spin h-4 w-4" />
            ) : (
              <FaTrophy className="h-4 w-4" />
            )}
            Recover MoM
          </button>
          <Link
            to="/dashboard/scheduler-jobs"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-500/30 hover:bg-primary-100 dark:hover:bg-primary-500/20 text-sm font-medium transition-colors"
          >
            <FaClipboardList className="h-4 w-4" />
            View Scheduler Runs
            <FaExternalLinkAlt className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Series selector */}
      <div className="mb-6">
        <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
          Series
        </label>
        <select
          value={selectedSeries}
          onChange={(e) => setSelectedSeries(e.target.value)}
          disabled={loadingSeries}
          className="w-full sm:w-96 px-4 py-2 rounded-lg border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">{loadingSeries ? 'Loading series...' : 'Select a series'}</option>
          {series.map((s) => (
            <option key={s.seriesId} value={s.seriesId}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* Summary strip */}
      {selectedSeries && contestsProgress.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <SummaryTile label="Contests" value={summary.contests} color="gray" />
          <SummaryTile label="Live Now" value={summary.liveContests} color="rose" pulse={summary.liveContests > 0} />
          <SummaryTile
            label="Matches"
            value={`${summary.completeMatches}/${summary.totalMatches}`}
            color="emerald"
            subtitle="complete"
          />
          <SummaryTile
            label="Live Matches"
            value={summary.liveMatches}
            color="rose"
            pulse={summary.liveMatches > 0}
          />
          <SummaryTile
            label="Points Ready"
            value={`${summary.pointsReady}/${summary.totalMatches}`}
            color="amber"
            subtitle="scored"
          />
        </div>
      )}

      {/* Manual actions (collapsible — hidden by default) */}
      {selectedSeries && (
        <div className="mb-6 bg-amber-50/60 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 rounded-xl overflow-hidden">
          <button
            onClick={() => setManualActionsOpen((v) => !v)}
            className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-amber-100/50 dark:hover:bg-amber-500/10 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FaCogs className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm font-bold text-amber-800 dark:text-amber-300">
                Manual Series Actions
              </span>
              <span className="text-xs text-amber-700/70 dark:text-amber-400/70">
                (escape hatch when automation fails)
              </span>
            </div>
            {manualActionsOpen ? (
              <FaChevronUp className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            ) : (
              <FaChevronDown className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            )}
          </button>
          {manualActionsOpen && (
            <div className="p-4 border-t border-amber-200 dark:border-amber-500/20 flex flex-wrap gap-2">
              <ActionButton
                onClick={handleProcessComplete}
                loading={processingComplete}
                icon={FaPlay}
                label="Process Complete"
                hint="Full sync + score"
              />
              <ActionButton
                onClick={handleSyncScorecards}
                loading={syncingScorecard}
                icon={FaSyncAlt}
                label="Sync Scorecards"
                hint="Refresh match scorecards"
              />
              <ActionButton
                onClick={handleUpdateFantasyPoints}
                loading={updatingFantasyPoints}
                icon={FaTrophy}
                label="Update Fantasy Points"
                hint="Rescore all contests"
              />
              <ActionButton
                onClick={handleRevertSeriesPoints}
                loading={revertingSeriesPoints}
                icon={FaUndoAlt}
                label="Revert Series"
                hint="Wipe all points in series"
                danger
              />
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loadingProgress && (
        <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
          <FaSyncAlt className="animate-spin h-5 w-5 mr-3" />
          Loading contest progress...
        </div>
      )}

      {/* Empty states */}
      {!loadingProgress && selectedSeries && contestsProgress.length === 0 && (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <FaTrophy className="mx-auto h-10 w-10 mb-3 opacity-30" />
          <p>No contests found for this series.</p>
        </div>
      )}

      {!loadingProgress && !selectedSeries && (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <FiActivity className="mx-auto h-10 w-10 mb-3 opacity-30" />
          <p>Select a series to view scoring progress.</p>
        </div>
      )}

      {/* Contest cards */}
      {!loadingProgress && contestsProgress.length > 0 && (
        <div className="space-y-4">
          {contestsProgress.map((contest) => (
            <ContestCard
              key={contest.contestId}
              contest={contest}
              rerunning={rerunContestId === contest.contestId}
              reverting={revertingContestId === contest.contestId}
              rebuilding={rebuildingContestId === contest.contestId}
              togglingLock={togglingLockContestId === contest.contestId}
              locked={!!lockStatuses[contest.contestId]}
              processingMatchId={processingMatchId}
              onForceRerun={() => handleForceRerun(contest.contestId, contest.contestName)}
              onRevertContest={() => handleRevertContest(contest.contestId, contest.contestName)}
              onRebuildContest={() => handleRebuildContest(contest.contestId, contest.contestName)}
              onToggleLock={() => handleToggleLock(contest.contestId, contest.contestName, !!lockStatuses[contest.contestId])}
              onForceUnlock={() => handleForceUnlock(contest.contestId, contest.contestName)}
              onViewAudit={() => handleViewAuditLog(contest.contestId, contest.contestName)}
              onEditTeams={() => handleOpenTeamsList(contest.contestId, contest.contestName)}
              onProcessMatch={(matchId) => handleProcessMatch(contest.contestId, matchId)}
              onRevertMatch={(matchId) => handleRevertMatch(contest.contestId, matchId)}
              getMatchStateColors={getMatchStateColors}
            />
          ))}
        </div>
      )}

      {/* Audit log dialog */}
      {auditLogFor && (
        <AuditLogDialog
          contestName={auditLogFor.contestName}
          entries={auditEntries}
          loading={auditLoading}
          onClose={() => setAuditLogFor(null)}
        />
      )}

      {/* Admin team-edit list dialog */}
      {teamsListFor && !editingTeam && (
        <TeamsListDialog
          contestName={teamsListFor.contestName}
          teams={contestTeams}
          loading={contestTeamsLoading}
          onEdit={(team) => handleOpenEditTeam(team)}
          onClose={() => { setTeamsListFor(null); setContestTeams([]); }}
        />
      )}

      {/* Admin team-edit dialog */}
      {editingTeam && (
        <EditTeamDialog
          team={editingTeam}
          squad={seriesSquad}
          squadLoading={seriesSquadLoading}
          matches={seriesMatches}
          onSubmit={handleSubmitTeamEdit}
          onApplyFromMatch={handleSubmitApplyFromMatch}
          onClose={() => setEditingTeam(null)}
        />
      )}
    </div>
  );
};

// ------- Sub-components -------

const SummaryTile: React.FC<{
  label: string;
  value: string | number;
  color: 'gray' | 'emerald' | 'rose' | 'amber';
  subtitle?: string;
  pulse?: boolean;
}> = ({ label, value, color, subtitle, pulse }) => {
  const colorMap = {
    gray: 'bg-gray-50 dark:bg-dark-300/30 text-gray-800 dark:text-gray-200',
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    rose: 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400',
    amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400',
  };
  return (
    <div className={`rounded-lg p-3 ${colorMap[color]}`}>
      <div className="flex items-center gap-1">
        {pulse && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
          </span>
        )}
        <p className="text-xl font-bold">{value}</p>
      </div>
      <p className="text-[11px] opacity-80 mt-0.5 uppercase tracking-wide">{label}</p>
      {subtitle && <p className="text-[10px] opacity-60">{subtitle}</p>}
    </div>
  );
};

const ActionButton: React.FC<{
  onClick: () => void;
  loading: boolean;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint?: string;
  danger?: boolean;
}> = ({ onClick, loading, icon: Icon, label, hint, danger }) => {
  const cls = danger
    ? 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30 hover:bg-red-200 dark:hover:bg-red-500/30'
    : 'bg-white dark:bg-dark-400 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-500/40 hover:bg-amber-100 dark:hover:bg-amber-500/10';
  return (
    <button
      onClick={onClick}
      disabled={loading}
      title={hint}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold border ${cls} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
    >
      {loading ? <FaSyncAlt className="animate-spin h-3 w-3" /> : <Icon className="h-3 w-3" />}
      {label}
    </button>
  );
};

const ContestCard: React.FC<{
  contest: ContestProgress;
  rerunning: boolean;
  reverting: boolean;
  rebuilding: boolean;
  togglingLock: boolean;
  locked: boolean;
  processingMatchId: string | null;
  onForceRerun: () => void;
  onRevertContest: () => void;
  onRebuildContest: () => void;
  onToggleLock: () => void;
  onForceUnlock: () => void;
  onViewAudit: () => void;
  onEditTeams: () => void;
  onProcessMatch: (matchId: string) => void;
  onRevertMatch: (matchId: string) => void;
  getMatchStateColors: (state?: string) => { bg: string; border: string; badge: string };
}> = ({
  contest,
  rerunning,
  reverting,
  rebuilding,
  togglingLock,
  locked,
  processingMatchId,
  onForceRerun,
  onRevertContest,
  onRebuildContest,
  onToggleLock,
  onForceUnlock,
  onViewAudit,
  onEditTeams,
  onProcessMatch,
  onRevertMatch,
  getMatchStateColors,
}) => {
  const hasLive = contest.matches.some((m) => LIVE_STATES.includes(m.state || ''));
  const processed = contest.matches.filter((m) => m.fantasyPoints).length;
  const total = contest.matches.length;
  // Matches where Cricbuzz has published MoM but this contest's points
  // don't include the man_of_match bonus — surfaces the gap that the
  // Recover MoM sweep fixes.
  const missedMom = contest.matches.filter((m) => m.momApplied === false).length;
  // Complete matches still missing MoM — either Cricbuzz hasn't published it
  // yet, or MatchDetails got stuck before final ingest (Step Function died
  // pre-finalization). Either way the empty-circle MoM indicator should
  // surface as a count so admins notice.
  const pendingMom = contest.matches.filter(
    (m) => m.state === 'Complete' && m.momApplied === null,
  ).length;
  // Matches in any terminal state whose fantasy points never finalized.
  // The {processed}/{total} chip can't distinguish "not played yet" from
  // "broken" — this chip flags only the broken case.
  const TERMINAL_STATES = ['Complete', 'Abandon', 'Cancelled', 'No Result'];
  const missingPoints = contest.matches.filter(
    (m) => TERMINAL_STATES.includes(m.state || '') && !m.fantasyPoints,
  ).length;

  // Auto-scroll the horizontal kanban strip to the boundary between the
  // most-recent completed match and the next upcoming one. Without this
  // every card opens scrolled to match #1, forcing the admin to swipe
  // through the entire series to reach today's tile.
  const stripRef = useRef<HTMLDivElement | null>(null);
  const focusRef = useRef<HTMLDivElement | null>(null);
  // Index to focus: prefer the first non-Complete match (= live or next
  // upcoming). If everything is Complete, focus the last one.
  const focusIndex = useMemo(() => {
    if (contest.matches.length === 0) return -1;
    const firstUpcoming = contest.matches.findIndex(
      (m) => m.state !== 'Complete',
    );
    return firstUpcoming === -1 ? contest.matches.length - 1 : firstUpcoming;
  }, [contest.matches]);
  useEffect(() => {
    if (focusIndex < 0) return;
    const el = focusRef.current;
    const strip = stripRef.current;
    if (!el || !strip) return;
    // Center the focus tile in its scrollable parent. Using
    // scrollIntoView with inline:'center' would scroll the whole page;
    // we want only the strip to scroll, hence manual offset math.
    const left = el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
  }, [focusIndex, contest.contestId]);

  return (
    <div className="bg-white dark:bg-dark-400/60 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-300/40 flex flex-wrap items-center gap-2">
        <h3 className="text-base font-bold text-gray-900 dark:text-white flex-1 min-w-[200px]">
          {contest.contestName}
        </h3>
        {hasLive && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-rose-500" />
            </span>
            LIVE
          </span>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
          {processed}/{total} scored
        </span>
        {missedMom > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
            title={`${missedMom} match(es) have MoM published in Cricbuzz but the man_of_match bonus is not applied to this contest. Click "Recover MoM" or "Rerun" to fix.`}
          >
            <FaTrophy className="h-2.5 w-2.5" />
            {missedMom} missed MoM
          </span>
        )}
        {pendingMom > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
            title={`${pendingMom} completed match(es) with no MoM captured yet. Either Cricbuzz hasn't published it or the match wasn't finalized (MatchDetails.isMatchComplete=false). Watchdog should auto-rescue; if it lingers, Process the match manually.`}
          >
            <FaClock className="h-2.5 w-2.5" />
            {pendingMom} pending MoM
          </span>
        )}
        {missingPoints > 0 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
            title={`${missingPoints} terminal-state match(es) whose fantasy points never finalized. Usually means MatchDetails wasn't marked complete (Step Function died pre-finalization). Click Process or wait for watchdog.`}
            data-testid="missing-points-badge"
          >
            <FaTimesCircle className="h-2.5 w-2.5" />
            {missingPoints} missing points
          </span>
        )}
        {locked && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300">
            <FaLock className="h-2.5 w-2.5" />
            LOCKED
          </span>
        )}
        <button
          onClick={onToggleLock}
          disabled={togglingLock}
          className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border disabled:opacity-50 ${
            locked
              ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-200 dark:hover:bg-emerald-500/30'
              : 'bg-slate-100 dark:bg-slate-500/20 text-slate-700 dark:text-slate-400 border-slate-200 dark:border-slate-500/30 hover:bg-slate-200 dark:hover:bg-slate-500/30'
          }`}
          title={locked ? 'Unlock teams (allow edits)' : 'Lock teams (prevent edits, start scoring)'}
        >
          {togglingLock ? (
            <FaSyncAlt className="animate-spin h-3 w-3" />
          ) : locked ? (
            <FaLockOpen className="h-3 w-3" />
          ) : (
            <FaLock className="h-3 w-3" />
          )}
          {locked ? 'Unlock' : 'Lock'}
        </button>
        {locked && (
          <button
            onClick={onForceUnlock}
            disabled={togglingLock}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30 hover:bg-amber-200 dark:hover:bg-amber-500/30 disabled:opacity-50"
            title="Force unlock — bypasses the 'can't unlock after series start' guard. Still rejects match_clash contests (by design)."
          >
            <FaLockOpen className="h-3 w-3" />
            Force Unlock
          </button>
        )}
        <button
          onClick={onEditTeams}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30 hover:bg-indigo-200 dark:hover:bg-indigo-500/30"
          title="Edit member fantasy teams (uses contest's transfer logic)"
        >
          <FaUserEdit className="h-3 w-3" />
          Edit Teams
        </button>
        <button
          onClick={onViewAudit}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-300 dark:border-dark-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-300/40"
          title="View audit log"
        >
          <FaClipboardList className="h-3 w-3" />
          Audit
        </button>
        <button
          onClick={onForceRerun}
          disabled={rerunning}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 hover:bg-amber-200 dark:hover:bg-amber-500/30 disabled:opacity-50"
          title="Force re-score every match"
        >
          {rerunning ? <FaSyncAlt className="animate-spin h-3 w-3" /> : <FaRedo className="h-3 w-3" />}
          Rerun
        </button>
        <button
          onClick={onRebuildContest}
          disabled={rebuilding}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 hover:bg-blue-200 dark:hover:bg-blue-500/30 disabled:opacity-50"
          title="Revert + re-score every match in order"
        >
          {rebuilding ? <FaSyncAlt className="animate-spin h-3 w-3" /> : <FaCogs className="h-3 w-3" />}
          Rebuild
        </button>
        <button
          onClick={onRevertContest}
          disabled={reverting}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30 hover:bg-red-200 dark:hover:bg-red-500/30 disabled:opacity-50"
          title="Revert all fantasy points for this contest"
        >
          {reverting ? <FaSyncAlt className="animate-spin h-3 w-3" /> : <FaUndoAlt className="h-3 w-3" />}
          Revert
        </button>
      </div>

      {/* Match tiles — horizontal kanban strip, scrolls left/right so each
          contest stays one row tall regardless of match count. */}
      <div className="p-4">
        {contest.matches.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">No matches yet.</p>
        ) : (
          <div ref={stripRef} className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
            {contest.matches.map((m, idx) => {
              const colors = getMatchStateColors(m.state);
              const busy = processingMatchId === `${contest.contestId}:${m.matchId}`;
              const isFocus = idx === focusIndex;
              return (
                <div
                  key={m.matchId}
                  ref={isFocus ? focusRef : undefined}
                  className={`rounded-lg border ${colors.border} ${colors.bg} p-2 flex flex-col gap-1 flex-shrink-0 w-48 snap-start`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400">
                      #{m.matchId}
                    </span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${colors.badge}`}>
                      {m.state}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                    {m.localTeam || '?'} v {m.visitorTeam || '?'}
                  </p>
                  <div className="flex items-center gap-2 text-[10px] flex-wrap">
                    <span className="flex items-center gap-0.5">
                      {m.scoreUpdated ? (
                        <FaCheckCircle className="h-2.5 w-2.5 text-emerald-500" />
                      ) : (
                        <FaTimesCircle className="h-2.5 w-2.5 text-gray-400" />
                      )}
                      Score
                    </span>
                    <span className="flex items-center gap-0.5">
                      {m.fantasyPoints ? (
                        <FaCheckCircle className="h-2.5 w-2.5 text-emerald-500" />
                      ) : (
                        <FaTimesCircle className="h-2.5 w-2.5 text-gray-400" />
                      )}
                      Points
                    </span>
                    {/* MoM indicator — green check if applied, red X if
                        published in Cricbuzz but missing in this contest's
                        points, dash if not expected (no MoM yet / no-result). */}
                    <span
                      className="flex items-center gap-0.5"
                      title={
                        m.momApplied === true
                          ? 'Man-of-the-match bonus applied'
                          : m.momApplied === false
                            ? 'MoM published but bonus NOT applied — use Recover MoM or Process'
                            : 'MoM not published yet'
                      }
                    >
                      {m.momApplied === true ? (
                        <FaCheckCircle className="h-2.5 w-2.5 text-emerald-500" />
                      ) : m.momApplied === false ? (
                        <FaTimesCircle className="h-2.5 w-2.5 text-red-500" />
                      ) : (
                        <span className="inline-block h-2.5 w-2.5 rounded-full border border-gray-300 dark:border-gray-600" />
                      )}
                      MoM
                    </span>
                  </div>
                  {/* Per-match manual actions */}
                  <div className="flex items-center gap-1 mt-1">
                    <button
                      onClick={() => onProcessMatch(m.matchId)}
                      disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-1.5 py-0.5 text-[10px] rounded border border-amber-300 dark:border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/10 disabled:opacity-50"
                      title="Re-process this match"
                    >
                      {busy ? <FaSyncAlt className="animate-spin h-2.5 w-2.5" /> : <FaRedo className="h-2.5 w-2.5" />}
                      Process
                    </button>
                    <button
                      onClick={() => onRevertMatch(m.matchId)}
                      disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-1.5 py-0.5 text-[10px] rounded border border-red-300 dark:border-red-500/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/10 disabled:opacity-50"
                      title="Revert this match"
                    >
                      <FaUndoAlt className="h-2.5 w-2.5" />
                      Revert
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ----- Admin team-edit dialogs ----- */

const TeamsListDialog: React.FC<{
  contestName: string;
  teams: any[];
  loading: boolean;
  onEdit: (team: any) => void;
  onClose: () => void;
}> = ({ contestName, teams, loading, onEdit, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-dark-400 rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 dark:border-dark-300/40 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <FaUsers className="h-4 w-4 text-indigo-500" />
              Edit Member Teams
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{contestName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="overflow-auto p-4 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <FaSyncAlt className="animate-spin h-5 w-5 mr-2" /> Loading teams…
            </div>
          ) : teams.length === 0 ? (
            <p className="text-sm text-gray-500 italic text-center py-12">No teams yet.</p>
          ) : (
            <div className="space-y-2">
              {teams.map((t) => (
                <div
                  key={t._id || t.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-dark-300/30 rounded-lg border border-gray-100 dark:border-dark-300/40"
                >
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-indigo-700 dark:text-indigo-400">
                      {(t.fantasyName || 'T').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">{t.fantasyName}</p>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {(t.players?.length || 0)} players
                      {t.totalPoints != null && ` · ${t.totalPoints} pts`}
                    </p>
                  </div>
                  <button
                    onClick={() => onEdit(t)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    <FaUserEdit className="h-3 w-3" />
                    Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EditTeamDialog: React.FC<{
  team: any;
  squad: any[];
  squadLoading: boolean;
  matches?: any[];
  onSubmit: (body: { players?: number[]; captainPlayerId?: number; viceCaptainPlayerId?: number; reason?: string; force?: boolean }) => Promise<void>;
  onApplyFromMatch?: (body: {
    fromMatchId: number;
    players: number[];
    captainPlayerId?: number | null;
    viceCaptainPlayerId?: number | null;
    reason?: string;
  }) => Promise<void>;
  onClose: () => void;
}> = ({ team, squad, squadLoading, matches = [], onSubmit, onApplyFromMatch, onClose }) => {
  const initialPlayers = useMemo<number[]>(
    () => (team.players || []).map((p: any) => Number(p)),
    [team.players],
  );

  // Last lineup snapshot (what's currently being scored). On a buffered-edit
  // contest team.players may have drifted from this — admin can revert.
  const lastLineup = useMemo(() => {
    const lineups: any[] = Array.isArray(team.lineups) ? team.lineups : [];
    return lineups.length ? lineups[lineups.length - 1] : null;
  }, [team.lineups]);

  const [selected, setSelected] = useState<Set<number>>(new Set(initialPlayers));
  const [captainId, setCaptainId] = useState<number | null>(team.captainPlayerId ?? null);
  const [vcId, setVcId] = useState<number | null>(team.viceCaptainPlayerId ?? null);
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Retroactive-apply mode. When on, submit goes to the new
  // /apply-from-match endpoint and applies the new lineup retroactively
  // to fromMatchId and onward, splitting whichever existing lineup
  // contains it. Only available when the parent passed the handler in.
  const [retroOn, setRetroOn] = useState(false);
  const [retroFromMatchId, setRetroFromMatchId] = useState<number | ''>('');

  // Build the list of matchIds present across the team's lineups,
  // enriched with matchOrderId + team names from the contest's matches
  // list so the dropdown can render "M36 · MI vs GT". Falls back to the
  // raw matchId if matches list isn't loaded yet.
  const matchMetaByMatchId = useMemo(() => {
    const map = new Map<number, { matchOrderId?: number; localTeam?: string; visitorTeam?: string }>();
    for (const m of matches) {
      const mid = Number(m.matchId ?? m.id);
      if (!mid) continue;
      map.set(mid, {
        matchOrderId: m.matchOrderId,
        localTeam: m.localTeam?.teamSName || m.localTeam?.teamName || (m as any)?.localTeamName,
        visitorTeam: m.visitorTeam?.teamSName || m.visitorTeam?.teamName || (m as any)?.visitorTeamName,
      });
    }
    return map;
  }, [matches]);

  const retroCandidateMatches = useMemo(() => {
    const lineups: any[] = Array.isArray(team.lineups) ? team.lineups : [];
    const seen = new Set<number>();
    const list: {
      matchId: number;
      lineupNumber: number;
      matchOrderId?: number;
      localTeam?: string;
      visitorTeam?: string;
    }[] = [];
    lineups.forEach((l, i) => {
      for (const mid of (l.matchIds || []).map((x: any) => Number(x))) {
        if (!seen.has(mid)) {
          seen.add(mid);
          const meta = matchMetaByMatchId.get(mid) || {};
          list.push({
            matchId: mid,
            lineupNumber: i + 1,
            matchOrderId: meta.matchOrderId,
            localTeam: meta.localTeam,
            visitorTeam: meta.visitorTeam,
          });
        }
      }
    });
    // Prefer matchOrderId for ordering when available — that's the
    // semantic ordering the engine uses. Fall back to matchId.
    list.sort((a, b) => {
      const ao = a.matchOrderId ?? a.matchId;
      const bo = b.matchOrderId ?? b.matchId;
      return ao - bo;
    });
    return list;
  }, [team.lineups, matchMetaByMatchId]);

  const targetSize = initialPlayers.length;

  const togglePlayer = (pid: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) {
        next.delete(pid);
        // If removed player was captain/vc, clear that role
        if (captainId === pid) setCaptainId(null);
        if (vcId === pid) setVcId(null);
      } else {
        next.add(pid);
      }
      return next;
    });
  };

  const resetToCurrent = () => {
    setSelected(new Set(initialPlayers));
    setCaptainId(team.captainPlayerId ?? null);
    setVcId(team.viceCaptainPlayerId ?? null);
  };

  const restoreLastLineup = () => {
    if (!lastLineup) return;
    const players = (lastLineup.players || []).map((p: any) => Number(p));
    setSelected(new Set(players));
    setCaptainId(lastLineup.captainPlayerId ?? null);
    setVcId(lastLineup.viceCaptainPlayerId ?? null);
  };

  // Squad enriched with selection state, then split into "current" (selected)
  // and "available" (not selected) so we can render side-by-side columns.
  const squadByPid = useMemo(() => {
    const m = new Map<number, any>();
    for (const p of squad) m.set(Number(p.playerId), p);
    return m;
  }, [squad]);

  const matchesSearch = (p: any) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = (p.playerName || '').toLowerCase();
    const role = (p.playerRole || '').toLowerCase();
    const teamName = (p.teamName || '').toLowerCase();
    return name.includes(q) || role.includes(q) || teamName.includes(q);
  };

  const initialSet = useMemo(() => new Set(initialPlayers), [initialPlayers]);

  // Current side: every selected pid, ordered as they were in `initialPlayers`
  // first (so the original lineup stays in stable order), then any new
  // additions appended.
  const currentColumn = useMemo(() => {
    const order: number[] = [];
    for (const pid of initialPlayers) {
      if (selected.has(pid)) order.push(pid);
    }
    for (const pid of Array.from(selected)) {
      if (!initialSet.has(pid)) order.push(pid);
    }
    return order
      .map((pid) => ({
        pid,
        player: squadByPid.get(pid),
        isAdded: !initialSet.has(pid),
      }))
      .filter((row) => !!row.player && matchesSearch(row.player));
  }, [selected, initialPlayers, initialSet, squadByPid, search]);

  // Available side: every squad player NOT selected, plus filter.
  // Players who were in the team before but are now removed are shown in
  // a "Removed" group at the top so admin can re-add them in one click.
  const availableColumn = useMemo(() => {
    return squad
      .filter((p: any) => !selected.has(Number(p.playerId)) && matchesSearch(p))
      .map((p: any) => ({
        pid: Number(p.playerId),
        player: p,
        wasInTeam: initialSet.has(Number(p.playerId)),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squad, selected, search, initialSet]);

  // Compute diff vs initial
  const playersIn = useMemo(
    () => Array.from(selected).filter((p) => !initialSet.has(p)),
    [selected, initialSet],
  );
  const playersOut = useMemo(
    () => initialPlayers.filter((p) => !selected.has(p)),
    [selected, initialPlayers],
  );

  // Diff vs last snapshot (what's actually being scored). When this is
  // non-zero, those are the "buffered" changes that will apply at the
  // next snapshot deadline.
  const lastLineupSet = useMemo(
    () => new Set<number>((lastLineup?.players || []).map((p: any) => Number(p))),
    [lastLineup],
  );
  const diffVsSnapshotIn = useMemo(
    () => Array.from(selected).filter((p) => !lastLineupSet.has(p)),
    [selected, lastLineupSet],
  );
  const diffVsSnapshotOut = useMemo(
    () => Array.from(lastLineupSet).filter((p) => !selected.has(p)),
    [selected, lastLineupSet],
  );

  const sizeMismatch = selected.size !== targetSize;
  const swapMismatch = playersIn.length !== playersOut.length;
  const captainOk = captainId == null || selected.has(captainId);
  const vcOk = vcId == null || selected.has(vcId);
  const captainVcDistinct = captainId == null || vcId == null || captainId !== vcId;
  // In retroactive mode the swap-balance check doesn't apply (the new
  // lineup replaces the existing one for fromMatch onward; the diff with
  // the prior live roster is informational only). Size + captain/VC
  // checks still apply.
  const retroReady = retroOn ? !!retroFromMatchId : true;
  const canSubmit =
    !sizeMismatch &&
    (retroOn ? true : !swapMismatch) &&
    captainOk &&
    vcOk &&
    captainVcDistinct &&
    retroReady &&
    !submitting;

  const playerLabel = (pid: number) => {
    const p = squadByPid.get(pid);
    return p?.playerName || `#${pid}`;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Retroactive path — only when the toggle is on AND a fromMatch is
      // selected AND the parent gave us a handler. Sends full roster +
      // captain/VC to /apply-from-match which splits the snapshot and
      // re-scores affected matches.
      if (retroOn && retroFromMatchId && onApplyFromMatch) {
        await onApplyFromMatch({
          fromMatchId: Number(retroFromMatchId),
          players: Array.from(selected),
          captainPlayerId: captainId ?? null,
          viceCaptainPlayerId: vcId ?? null,
          reason: reason || undefined,
        });
        return;
      }

      const body: any = { reason: reason || undefined, force };
      if (playersIn.length > 0 || playersOut.length > 0) {
        body.players = Array.from(selected);
      }
      if (captainId !== team.captainPlayerId) body.captainPlayerId = captainId ?? undefined;
      if (vcId !== team.viceCaptainPlayerId) body.viceCaptainPlayerId = vcId ?? undefined;
      await onSubmit(body);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-dark-400 rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 dark:border-dark-300/40 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">Edit {team.fantasyName}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {selected.size}/{targetSize} selected
              {playersIn.length > 0 && ` · ${playersIn.length} in / ${playersOut.length} out`}
              {lastLineup && (diffVsSnapshotIn.length > 0 || diffVsSnapshotOut.length > 0) && (
                <span className="ml-2 text-amber-500 dark:text-amber-400">
                  · {Math.max(diffVsSnapshotIn.length, diffVsSnapshotOut.length)} change(s) vs last snapshot
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={resetToCurrent}
              className="px-2.5 py-1 text-[11px] font-medium rounded border border-gray-300 dark:border-dark-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-300/40"
              title="Discard changes — revert to the team's current live roster (team.players)"
            >
              Reset
            </button>
            {lastLineup && (
              <button
                onClick={restoreLastLineup}
                className="px-2.5 py-1 text-[11px] font-medium rounded border border-amber-300 dark:border-amber-500/30 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                title="Restore the players + captain/VC from the most recent locked match snapshot. Useful for rolling back a problematic transfer."
              >
                Restore last snapshot
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none px-2"
            >
              ×
            </button>
          </div>
        </div>

        {/* Search + force flag */}
        <div className="px-6 py-3 border-b border-gray-100 dark:border-dark-300/40 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <FaSearch className="h-3 w-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, role, team"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-2 py-1 text-xs rounded border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            Force (skip transfer-limit cap)
          </label>
          {onApplyFromMatch && retroCandidateMatches.length > 0 && (
            <label className="inline-flex items-center gap-1.5 text-xs text-rose-700 dark:text-rose-400 cursor-pointer">
              <input
                type="checkbox"
                checked={retroOn}
                onChange={(e) => setRetroOn(e.target.checked)}
                className="rounded border-gray-300 text-rose-600 focus:ring-rose-500"
              />
              Apply retroactively from match
            </label>
          )}
          {retroOn && onApplyFromMatch && retroCandidateMatches.length > 0 && (
            <select
              value={retroFromMatchId}
              onChange={(e) =>
                setRetroFromMatchId(e.target.value ? Number(e.target.value) : '')
              }
              className="px-2 py-1 text-xs rounded border border-rose-300 dark:border-rose-500/30 bg-white dark:bg-dark-400 text-gray-900 dark:text-gray-100"
              title="The new roster will apply from this match onward. Affected matches that have already been played will be re-scored."
            >
              <option value="">— pick fromMatch —</option>
              {retroCandidateMatches.map((m) => {
                const seq = m.matchOrderId != null ? `M${m.matchOrderId}` : `id ${m.matchId}`;
                const teams =
                  m.localTeam && m.visitorTeam
                    ? `${m.localTeam} vs ${m.visitorTeam}`
                    : '';
                return (
                  <option key={m.matchId} value={m.matchId}>
                    {seq}
                    {teams ? ` · ${teams}` : ''} (lineup #{m.lineupNumber})
                  </option>
                );
              })}
            </select>
          )}
        </div>
        {retroOn && (
          <div className="px-6 pb-3 -mt-1">
            <div className="rounded-md border border-rose-300 dark:border-rose-500/30 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-300">
              Retroactive mode will <strong>split the lineup snapshot</strong>{' '}
              at the chosen match and <strong>re-score</strong> any affected
              matches that have already been played. Use only for support
              cases — buffered edits in the normal admin edit flow can't help
              once the relevant deadline has passed.
            </div>
          </div>
        )}

        {/* Two-column body: Current Team | Available squad */}
        <div className="flex-1 min-h-[320px] grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-dark-300/40 overflow-hidden">
          {/* CURRENT TEAM column */}
          <div className="flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 dark:bg-dark-300/20 border-b border-gray-100 dark:border-dark-300/30 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                Current Team
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                {currentColumn.length}/{targetSize}
                {playersIn.length > 0 && ` · ${playersIn.length} added`}
              </span>
            </div>
            <div className="overflow-auto p-3 space-y-1.5 flex-1">
              {currentColumn.length === 0 ? (
                <p className="text-xs text-gray-500 italic text-center py-6">
                  {search ? 'No selected players match search.' : 'Roster is empty — pick from Available.'}
                </p>
              ) : (
                currentColumn.map(({ pid, player, isAdded }) => (
                  <button
                    key={pid}
                    onClick={() => togglePlayer(pid)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left transition-colors group ${
                      isAdded
                        ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
                        : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30'
                    } hover:opacity-80`}
                    title={isAdded ? 'New addition — click to remove' : 'In team — click to remove'}
                  >
                    <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-dark-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-gray-700 dark:text-gray-300">
                        {(player.playerName || '?').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                        {player.playerName}
                        {isAdded && (
                          <span className="text-[8px] font-bold uppercase px-1 py-px rounded bg-blue-200 dark:bg-blue-500/30 text-blue-800 dark:text-blue-200">
                            new
                          </span>
                        )}
                        {captainId === pid && (
                          <span className="text-[8px] font-bold px-1 py-px rounded bg-amber-500 text-black">C</span>
                        )}
                        {vcId === pid && (
                          <span className="text-[8px] font-bold px-1 py-px rounded bg-blue-500 text-white">VC</span>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                        {player.playerRole}{player.teamName && ` · ${player.teamName}`}
                      </p>
                    </div>
                    <span className="text-rose-500 dark:text-rose-400 font-bold text-sm opacity-50 group-hover:opacity-100">−</span>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* AVAILABLE column */}
          <div className="flex flex-col overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 dark:bg-dark-300/20 border-b border-gray-100 dark:border-dark-300/30 flex items-center justify-between">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                Available
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">
                {availableColumn.length} player{availableColumn.length === 1 ? '' : 's'}
                {playersOut.length > 0 && ` · ${playersOut.length} removed from original`}
              </span>
            </div>
            <div className="overflow-auto p-3 space-y-1.5 flex-1">
              {squadLoading ? (
                <div className="flex items-center justify-center py-12 text-gray-500">
                  <FaSyncAlt className="animate-spin h-5 w-5 mr-2" /> Loading squad…
                </div>
              ) : availableColumn.length === 0 ? (
                <p className="text-xs text-gray-500 italic text-center py-6">
                  {search ? 'No available players match search.' : 'Every squad player is already in the team.'}
                </p>
              ) : (
                availableColumn.map(({ pid, player, wasInTeam }) => (
                  <button
                    key={pid}
                    onClick={() => togglePlayer(pid)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg border text-left transition-colors group ${
                      wasInTeam
                        ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 opacity-80'
                        : 'bg-white dark:bg-dark-400 border-gray-200 dark:border-dark-300 hover:bg-gray-50 dark:hover:bg-dark-300/40'
                    }`}
                    title={wasInTeam ? 'Removed from original — click to put back' : 'Available — click to add'}
                  >
                    <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-dark-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-gray-700 dark:text-gray-300">
                        {(player.playerName || '?').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                        {player.playerName}
                        {wasInTeam && (
                          <span className="text-[8px] font-bold uppercase px-1 py-px rounded bg-rose-200 dark:bg-rose-500/30 text-rose-800 dark:text-rose-200">
                            removed
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                        {player.playerRole}{player.teamName && ` · ${player.teamName}`}
                      </p>
                    </div>
                    <span className="text-emerald-500 dark:text-emerald-400 font-bold text-sm opacity-50 group-hover:opacity-100">+</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Captain / VC + reason + submit */}
        <div className="px-6 py-3 border-t border-gray-100 dark:border-dark-300/40 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select
              value={captainId ?? ''}
              onChange={(e) => setCaptainId(e.target.value ? Number(e.target.value) : null)}
              className="px-3 py-2 text-xs rounded border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-900 dark:text-gray-100"
            >
              <option value="">— Captain —</option>
              {Array.from(selected).map((pid) => (
                <option key={pid} value={pid}>{playerLabel(pid)} (C)</option>
              ))}
            </select>
            <select
              value={vcId ?? ''}
              onChange={(e) => setVcId(e.target.value ? Number(e.target.value) : null)}
              className="px-3 py-2 text-xs rounded border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-900 dark:text-gray-100"
            >
              <option value="">— Vice-Captain —</option>
              {Array.from(selected).map((pid) => (
                <option key={pid} value={pid}>{playerLabel(pid)} (VC)</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            placeholder="Reason (e.g. 'support ticket #1234')"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 text-xs rounded border border-gray-300 dark:border-dark-300 bg-white dark:bg-dark-400 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {!captainVcDistinct && (
            <p className="text-[11px] text-red-500">Captain and Vice-Captain must be different players.</p>
          )}
          {sizeMismatch && (
            <p className="text-[11px] text-red-500">Roster must keep {targetSize} players (currently {selected.size}).</p>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              className="px-3 py-2 text-xs font-medium rounded border border-gray-300 dark:border-dark-300 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-300/40"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1 px-4 py-2 text-xs font-bold rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <FaSyncAlt className="animate-spin h-3 w-3" /> : <FaUserEdit className="h-3 w-3" />}
              {(() => {
                if (submitting) return retroOn ? 'Applying…' : 'Saving…';
                if (!retroOn) return 'Save Edit';
                const meta = retroFromMatchId ? matchMetaByMatchId.get(Number(retroFromMatchId)) : null;
                const label = meta?.matchOrderId != null ? `M${meta.matchOrderId}` : `M?`;
                return `Apply from ${label}`;
              })()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AuditLogDialog: React.FC<{
  contestName: string;
  entries: any[];
  loading: boolean;
  onClose: () => void;
}> = ({ contestName, entries, loading, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-dark-400 rounded-xl border border-gray-200 dark:border-dark-300/40 shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-100 dark:border-dark-300/40 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-gray-900 dark:text-white">Audit Log</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{contestName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <div className="overflow-auto p-4 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <FaSyncAlt className="animate-spin h-5 w-5 mr-2" /> Loading…
            </div>
          ) : entries.length === 0 ? (
            <p className="text-sm text-gray-500 italic text-center py-12">No audit entries.</p>
          ) : (
            <div className="space-y-2">
              {entries.map((e, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 dark:bg-dark-300/30 rounded-md p-3 border border-gray-100 dark:border-dark-300/40"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                      {e.action || e.event || 'entry'}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}
                    </span>
                  </div>
                  {e.details && (
                    <pre className="text-[10px] text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words">
                      {typeof e.details === 'string' ? e.details : JSON.stringify(e.details, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tracker;
